import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, Medal, Award } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface DebateScore {
  user_id: string;
  wins: number;
  losses: number;
  total_debates: number;
  profile?: {
    display_name: string;
    avatar_url: string | null;
    username: string | null;
  };
}

export const DebateScoreboard: React.FC<{ limit?: number }> = ({ limit = 10 }) => {
  const { t } = useTranslation();

  const { data: scores = [], isLoading } = useQuery({
    queryKey: ['debate-scoreboard', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('debate_scores')
        .select('*')
        .order('wins', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching scoreboard:', error);
        return [];
      }

      // Fetch profiles for each user
      const scoresWithProfiles = await Promise.all(
        (data || []).map(async (score) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name, avatar_url, username')
            .eq('user_id', score.user_id)
            .single();

          return { ...score, profile };
        })
      );

      return scoresWithProfiles as DebateScore[];
    },
  });

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Trophy className="h-5 w-5 text-yellow-500" />;
      case 1:
        return <Medal className="h-5 w-5 text-gray-400" />;
      case 2:
        return <Award className="h-5 w-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-sm font-medium text-muted-foreground">{index + 1}</span>;
    }
  };

  const getWinRate = (wins: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((wins / total) * 100);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {t('debates.scoreboard', 'Scoreboard')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 animate-pulse">
                <div className="w-8 h-8 bg-muted rounded-full" />
                <div className="flex-1 h-4 bg-muted rounded" />
                <div className="w-16 h-4 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (scores.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5" />
            {t('debates.scoreboard', 'Scoreboard')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm text-center py-4">
            {t('debates.noScoresYet', 'Ingen fullførte debatter ennå')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          {t('debates.scoreboard', 'Scoreboard')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {scores.map((score, index) => (
            <div
              key={score.user_id}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors"
            >
              <div className="flex-shrink-0 w-6">
                {getRankIcon(index)}
              </div>
              
              <Avatar className="h-8 w-8">
                <AvatarImage src={score.profile?.avatar_url || undefined} />
                <AvatarFallback>
                  {score.profile?.display_name?.charAt(0) || '?'}
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">
                  {score.profile?.display_name || score.profile?.username || 'Ukjent'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {score.total_debates} {t('debates.debatesCompleted', 'debatter')}
                </p>
              </div>

              <div className="text-right">
                <p className="font-bold text-primary">{score.wins} {t('debates.wins', 'seire')}</p>
                <p className="text-xs text-muted-foreground">
                  {getWinRate(score.wins, score.total_debates)}% {t('debates.winRate', 'vinnerprosent')}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default DebateScoreboard;